import { useState, useEffect, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import {
    Box,
    Button,
    Typography,
    CircularProgress,
    IconButton,
    Alert,
    Snackbar,
    Stack,
    TextField,
    Slider,
    Tooltip,
    Divider,
    Chip,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import SaveIcon from "@mui/icons-material/Save";
import { ToolPageShell } from "./ToolPageShell";
import { TOOLS } from "../util/config";
import { colors } from "../theme/theme";
import { pdfEditApi, statsApi } from "../api/client";

const tool = TOOLS.find((t) => t.id === "pdf-edit");

// Backend renders pixmap at 2x zoom (fitz.Matrix(2.0, 2.0))

// ── color helpers ───────────────────────────────────────────────────────────
const rgbToHex = (r, g, b) => {
    const toHex = (n) => Math.round(Math.max(0, Math.min(1, n)) * 255).toString(16).padStart(2, "0");
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

const hexToRgb = (hex) => {
    const m = hex.replace("#", "");
    const r = parseInt(m.substring(0, 2), 16) / 255;
    const g = parseInt(m.substring(2, 4), 16) / 255;
    const b = parseInt(m.substring(4, 6), 16) / 255;
    return [r, g, b];
};

export const PdfEditPage = () => {
    const [session, setSession] = useState(null);
    const [pdfInfo, setPdfInfo] = useState(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [pageData, setPageData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [selectedBlock, setSelectedBlock] = useState(null);
    const [editText, setEditText] = useState("");
    const [editFontSize, setEditFontSize] = useState(12);
    const [editColor, setEditColor] = useState("#000000");
    const [saving, setSaving] = useState(false);
    const [zoom, setZoom] = useState(1.0);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    const showSnackbar = (message, severity) => setSnackbar({ open: true, message, severity });

    // ── Upload ──────────────────────────────────────────────────────────────
    const onDrop = useCallback(async (acceptedFiles) => {
        const file = acceptedFiles[0];
        if (!file || !file.name.toLowerCase().endsWith(".pdf")) {
            return showSnackbar("Please upload a valid PDF file", "error");
        }
        setUploading(true);
        try {
            const data = await pdfEditApi.upload(file);
            setSession(data.session_id);
            setPdfInfo(data);
            setCurrentPage(0);
            setSelectedBlock(null);
            showSnackbar(`"${data.filename}" loaded — ${data.pages} page(s)`, "success");
        } catch (e) {
            console.error(e);
            showSnackbar("Upload failed. Is the editor backend running on :8000?", "error");
        } finally {
            setUploading(false);
        }
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { "application/pdf": [".pdf"] },
        multiple: false,
    });

    // ── Load page when session / page changes ───────────────────────────────
    const loadPage = useCallback(async (pageNum) => {
        if (!session) return;
        setLoading(true);
        setSelectedBlock(null);
        try {
            const data = await pdfEditApi.getPage(session, pageNum);
            setPageData(data);
        } catch (e) {
            console.error(e);
            showSnackbar("Failed to load page", "error");
        } finally {
            setLoading(false);
        }
    }, [session]);

    useEffect(() => {
        if (session && pdfInfo) loadPage(currentPage);
    }, [session, currentPage, pdfInfo, loadPage]);

    // ── Select a text block ─────────────────────────────────────────────────
    const selectBlock = (block) => {
        setSelectedBlock(block);
        setEditText(block.text);
        setEditFontSize(block.font_size);
        const [r, g, b] = block.color;
        setEditColor(rgbToHex(r, g, b));
    };

    // ── Apply edit ──────────────────────────────────────────────────────────
    const applyEdit = async () => {
        if (!selectedBlock || !session) return;
        setSaving(true);
        try {
            const [r, g, b] = hexToRgb(editColor);
            const data = await pdfEditApi.edit({
                session_id: session,
                page: currentPage,
                block_index: selectedBlock.block_index,
                new_text: editText,
                x: selectedBlock.x,
                y: selectedBlock.y,
                width: selectedBlock.width,
                height: selectedBlock.height,
                font_size: editFontSize,
                font_color: [r, g, b],
            });
            setPageData((prev) => ({ ...prev, image: data.image }));
            await loadPage(currentPage);
            showSnackbar("Changes applied", "success");
        } catch (e) {
            console.error(e);
            showSnackbar("Edit failed: " + e.message, "error");
        } finally {
            setSaving(false);
        }
    };

    // ── Download ────────────────────────────────────────────────────────────
    const handleDownload = async () => {
        if (!session) return;
        try {
            const filename = pdfInfo?.filename ? `edited-${pdfInfo.filename}` : "edited.pdf";
            const blob = await pdfEditApi.download(session, filename);
            statsApi.logExport(tool.id, blob.size);
            showSnackbar("PDF downloaded", "success");
        } catch (e) {
            console.error(e);
            showSnackbar("Download failed", "error");
        }
    };

    // ── Close (reset state) ─────────────────────────────────────────────────
    const closeSession = () => {
        setSession(null);
        setPdfInfo(null);
        setPageData(null);
        setSelectedBlock(null);
        setCurrentPage(0);
    };

    // ── Render: upload zone ─────────────────────────────────────────────────
    if (!session) {
        return (
            <ToolPageShell
                tool={tool}
                tips={[
                    "Drop in a PDF to detect editable text blocks.",
                    "Click any block to change its text, size, or color.",
                    "Apply edits and download the modified PDF.",
                ]}
            >
                <Box
                    {...getRootProps()}
                    sx={{
                        border: "2px dashed",
                        borderColor: isDragActive ? "primary.main" : colors.border,
                        borderRadius: 2,
                        padding: 6,
                        textAlign: "center",
                        cursor: "pointer",
                        bgcolor: isDragActive ? colors.accentSoft : colors.surfaceAlt,
                        transition: "all 0.2s",
                        "&:hover": { borderColor: "primary.main", bgcolor: colors.accentSoft },
                    }}
                >
                    <input {...getInputProps()} />
                    {uploading ? (
                        <Stack alignItems="center" spacing={1}>
                            <CircularProgress />
                            <Typography variant="caption" sx={{ color: colors.textMuted }}>
                                Analyzing PDF text blocks...
                            </Typography>
                        </Stack>
                    ) : (
                        <>
                            <CloudUploadIcon sx={{ fontSize: 56, color: isDragActive ? "primary.main" : colors.textMuted, mb: 1 }} />
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {isDragActive ? "Drop your PDF" : "Drag & drop a PDF to edit"}
                            </Typography>
                            <Typography variant="body2" sx={{ color: colors.textMuted }}>
                                or click to browse
                            </Typography>
                        </>
                    )}
                </Box>
            </ToolPageShell>
        );
    }

    // ── Render: editor view ─────────────────────────────────────────────────
    return (
        <ToolPageShell
            tool={tool}
            tips={[
                `Page ${currentPage + 1} of ${pdfInfo.pages} — ${pageData?.blocks?.length || 0} editable blocks.`,
                "Click a highlighted text block on the page to edit it.",
                "Click Apply Changes, then Download when finished.",
            ]}
        >
            {/* File info header */}
            <Stack direction="row" alignItems="center" spacing={1.5} mb={2} flexWrap="wrap" useFlexGap>
                <Chip
                    label={pdfInfo.filename}
                    size="small"
                    sx={{
                        bgcolor: colors.accentSoft,
                        color: tool.accent,
                        fontWeight: 600,
                        fontFamily: '"JetBrains Mono"',
                        fontSize: 11,
                    }}
                />
                <Typography variant="caption" sx={{ color: colors.textMuted, fontFamily: '"JetBrains Mono"' }}>
                    {pdfInfo.pages} page{pdfInfo.pages > 1 ? "s" : ""}
                </Typography>
                <Box flex={1} />
                <Button
                    size="small"
                    variant="contained"
                    onClick={handleDownload}
                    startIcon={<DownloadIcon />}
                >
                    Download
                </Button>
                <IconButton size="small" onClick={closeSession}>
                    <CloseIcon />
                </IconButton>
            </Stack>

            <Divider sx={{ mb: 2 }} />

            {/* Toolbar: page nav + zoom */}
            <Stack direction="row" alignItems="center" spacing={1} mb={2} flexWrap="wrap" useFlexGap>
                <Tooltip title="Previous page">
                    <span>
                        <IconButton size="small" disabled={currentPage === 0} onClick={() => setCurrentPage((p) => p - 1)}>
                            <ArrowBackIosNewIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                <Typography sx={{ fontFamily: '"JetBrains Mono"', fontSize: 13, fontWeight: 600, minWidth: 60, textAlign: "center" }}>
                    {String(currentPage + 1).padStart(2, "0")} / {String(pdfInfo.pages).padStart(2, "0")}
                </Typography>
                <Tooltip title="Next page">
                    <span>
                        <IconButton size="small" disabled={currentPage >= pdfInfo.pages - 1} onClick={() => setCurrentPage((p) => p + 1)}>
                            <ArrowForwardIosIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>

                <Box flex={1} />

                <Tooltip title="Zoom out">
                    <span>
                        <IconButton size="small" disabled={zoom <= 0.5} onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.1).toFixed(2)))}>
                            <ZoomOutIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                <Typography sx={{ fontFamily: '"JetBrains Mono"', fontSize: 12, minWidth: 48, textAlign: "center", color: colors.textMuted }}>
                    {Math.round(zoom * 100)}%
                </Typography>
                <Tooltip title="Zoom in">
                    <span>
                        <IconButton size="small" disabled={zoom >= 2.0} onClick={() => setZoom((z) => Math.min(2.0, +(z + 0.1).toFixed(2)))}>
                            <ZoomInIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            </Stack>

            {/* Editor split: viewer + side panel */}
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: selectedBlock ? "1fr 280px" : "1fr" }, gap: 2 }}>
                {/* PDF viewer */}
                <Box
                    sx={{
                        bgcolor: colors.surfaceAlt,
                        borderRadius: 2,
                        border: `1px solid ${colors.border}`,
                        p: 2,
                        overflow: "auto",
                        maxHeight: 640,
                        position: "relative",
                    }}
                >
                    {loading ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
                            <CircularProgress />
                        </Box>
                    ) : pageData ? (
                        <Box
                            sx={{
                                position: "relative",
                                width: pageData.width * zoom,
                                margin: "0 auto",
                                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                            }}
                        >
                            <img
                                src={`data:image/png;base64,${pageData.image}`}
                                alt={`Page ${currentPage + 1}`}
                                style={{ width: "100%", display: "block", borderRadius: 4 }}
                            />
                            {/* Clickable text-block overlays */}
                            {pageData.blocks.map((block) => {
                                // Block coords are in PDF points; image is rendered at IMG_SCALE.
                                // Viewer image is then displayed at `zoom` of pageData.width.
                                // So overlay position in viewer = block coord in points × zoom.
                                const isSelected = selectedBlock?.index === block.index;
                                return (
                                    <Box
                                        key={block.index}
                                        onClick={() => selectBlock(block)}
                                        sx={{
                                            position: "absolute",
                                            left: block.x * zoom,
                                            top: block.y * zoom,
                                            width: block.width * zoom,
                                            height: block.height * zoom,
                                            cursor: "pointer",
                                            border: "1px solid",
                                            borderColor: isSelected ? tool.accent : "transparent",
                                            bgcolor: isSelected ? `${tool.accent}22` : "transparent",
                                            transition: "all 0.1s",
                                            "&:hover": {
                                                borderColor: tool.accent,
                                                bgcolor: `${tool.accent}15`,
                                            },
                                        }}
                                        title={block.text}
                                    />
                                );
                            })}
                        </Box>
                    ) : (
                        <Typography sx={{ color: colors.textMuted, textAlign: "center", py: 4 }}>
                            No page data
                        </Typography>
                    )}
                </Box>

                {/* Side panel */}
                {selectedBlock && (
                    <Box
                        sx={{
                            bgcolor: colors.surface,
                            border: `1px solid ${colors.border}`,
                            borderRadius: 2,
                            p: 2.5,
                            alignSelf: "start",
                            position: { md: "sticky" },
                            top: { md: 16 },
                        }}
                    >
                        <Typography variant="overline" sx={{ color: colors.textMuted }}>
                            Edit text block
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: colors.textMuted, fontFamily: '"JetBrains Mono"', mb: 2 }}>
                            #{selectedBlock.index} · {selectedBlock.font || "Helvetica"}
                        </Typography>

                        <TextField
                            label="Text"
                            multiline
                            minRows={3}
                            fullWidth
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            sx={{ mb: 2 }}
                        />

                        <Typography variant="caption" sx={{ color: colors.textMuted, display: "block", mb: 0.5 }}>
                            Font size: <strong>{editFontSize.toFixed(1)} pt</strong>
                        </Typography>
                        <Slider
                            value={editFontSize}
                            onChange={(_, v) => setEditFontSize(v)}
                            min={6}
                            max={72}
                            step={0.5}
                            size="small"
                            sx={{ color: tool.accent, mb: 2 }}
                        />

                        <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                            <Typography variant="caption" sx={{ color: colors.textMuted, flex: 1 }}>
                                Color
                            </Typography>
                            <input
                                type="color"
                                value={editColor}
                                onChange={(e) => setEditColor(e.target.value)}
                                style={{
                                    width: 40, height: 28,
                                    border: `1px solid ${colors.border}`,
                                    borderRadius: 4, cursor: "pointer", padding: 0, background: "none",
                                }}
                            />
                            <Typography sx={{ fontFamily: '"JetBrains Mono"', fontSize: 11, color: colors.textMuted }}>
                                {editColor.toUpperCase()}
                            </Typography>
                        </Stack>

                        <Button
                            fullWidth
                            variant="contained"
                            onClick={applyEdit}
                            disabled={saving}
                            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                        >
                            {saving ? "Applying..." : "Apply Changes"}
                        </Button>
                        <Button
                            fullWidth
                            size="small"
                            onClick={() => setSelectedBlock(null)}
                            sx={{ mt: 1, color: colors.textMuted }}
                        >
                            Deselect
                        </Button>
                    </Box>
                )}
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
            </Snackbar>
        </ToolPageShell>
    );
};